import './ClClickableAddress.css';

import React from 'react';
import classname from '@search/classname/src';
import { Tooltip, useTooltip } from '@vtblife/uikit';
import Icon from '@vtblife/uikit-icons';

const cn = classname.bind(null, 'ClClickableAddress');

export function ClClickableAddress({
    addresses,
    formattedAddressLong,
    metersToCenterText,
    metersToCenterClick,
    variant,
    noClickable,
    isCopiable = false,
    isMobile = false,
    preWrap
}: {
    preWrap?: boolean;
    noClickable?: boolean;
    isCopiable?: boolean;
    isMobile?: boolean;
    variant?: 'secondary' | 'narrow';
    addresses: {
        href?: string;
        content: string;
        isNofollowLink?: boolean;
    }[];
    formattedAddressLong: string;
    metersToCenterText?: string;
    metersToCenterClick?: () => void;
}) {
    const [ copiable, setCopiable ] = React.useState(false);
    const [ isHover, setIsHover ] = React.useState(false);
    const address = formattedAddressLong ?? addresses.map(item => item.content).join(', ');
    const copy = React.useCallback(async () => {
        // eslint-disable-next-line no-undef
        await navigator?.clipboard?.writeText(address)
            .then(() => setCopiable(true))
            .then(() => setTimeout(() => setCopiable(false), 5000))
            .catch(() => setCopiable(false));
    }, [ address ]);
    const { to, registerControl } = useTooltip();

    return (
        <div className={cn('links', { secondary: variant === 'secondary', narrow: variant === 'narrow' })}>
            {isCopiable && <span
                onMouseMove={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                onClick={copy}
                className={cn('copy', { isHover, isMobile })}
                ref={registerControl}
            >
                {/* eslint-disable-next-line max-len */}
                {copiable ?
                    <span className={cn('icon', { isMobile })}>
                        <Icon
                            name='check'
                            size='m'
                            color='rgba(116, 109, 147, 0.5)'
                        />
                    </span> :
                    <span className={cn('icon', { isMobile })}>
                        <Icon
                            name='copy'
                            size='m'
                            color={isHover ? 'rgb(39, 17, 136)' : 'rgba(116, 109, 147, 0.5)'}
                        />
                    </span>
                }
                {! isMobile && <Tooltip to={to}>
                    {copiable ? 'Адрес скопирован' : 'Скопировать'}
                </Tooltip>}
            </span>}
            {addresses.map((item, index, array) => (
                <span
                    key={index}
                    className={cn('hover', { isHover })}
                >
                    {! item.href ? item.content : (
                        <a
                            href={item.href}
                            className={cn('link', { preWrap })}
                            onClick={e => {
                                if (noClickable) e.preventDefault();
                            }}
                            data-noclick={noClickable ? '1' : undefined}
                            data-test='offer-address'
                            rel={item.isNofollowLink ? 'nofollow' : undefined}
                        >
                            {item.content}
                        </a>
                    )}
                    {index < array.length - 1 && ','}
                </span>
            ))}
            {! addresses.length && (
                <span className={cn('hover', { isHover })}>
                    {formattedAddressLong}
                </span>
            )}

            {metersToCenterText && (
                <MetersToCenter
                    noClickable={noClickable}
                    metersToCenterClick={metersToCenterClick}
                    metersToCenterText={metersToCenterText}
                />
            )}
        </div>
    );
}

function MetersToCenter({
    metersToCenterText,
    metersToCenterClick,
    noClickable
}: {
    noClickable?: boolean;
    metersToCenterText: string;
    metersToCenterClick?: () => void;
}) {
    const { to, registerControl } = useTooltip();

    return (
        <span
            ref={registerControl}
            onClick={noClickable ? undefined : metersToCenterClick}
            data-noclick={noClickable ? '1' : undefined}
            className={metersToCenterClick ? cn('link') : undefined}
        >{'\u00A0'}•{'\u00A0\u00A0'}{metersToCenterText}
            <Tooltip to={to} placement='body'>Расстояние по прямой от адреса <br /> до центра города</Tooltip>
        </span>
    );
}
